import { useCallback } from 'react';

export const useValidation = (form, th, isDealer, isVorMode, editMode) => {
    const requireDealerAddressSelection = !isDealer || isVorMode;
    const isKmInvalid = form.kmInformation !== null && form.kmInformation !== undefined && !Number.isInteger(form.kmInformation);

    const isSolutionDateAfterErrorNotificationDate = useCallback(() => {
        if (!form.problemSolved || !form.solutionDate || !form.errorNotificationDate) {
            return true;
        }

        const solutionDate = new Date(form.solutionDate);
        const errorNotificationDate = new Date(form.errorNotificationDate);

        if (Number.isNaN(solutionDate.getTime()) || Number.isNaN(errorNotificationDate.getTime())) {
            return false;
        }

        solutionDate.setHours(0, 0, 0, 0);
        errorNotificationDate.setHours(0, 0, 0, 0);

        return solutionDate.getTime() >= errorNotificationDate.getTime();
    }, [form.problemSolved, form.solutionDate, form.errorNotificationDate]);

    const isVisitDateAfterErrorNotificationDate = useCallback(() => {
        if (!form.onSiteSupport || isDealer || !form.visitDate || !form.errorNotificationDate) {
            return true;
        }

        const visitDate = new Date(form.visitDate);
        const errorNotificationDate = new Date(form.errorNotificationDate);

        if (Number.isNaN(visitDate.getTime()) || Number.isNaN(errorNotificationDate.getTime())) {
            return false;
        }

        visitDate.setHours(0, 0, 0, 0);
        errorNotificationDate.setHours(0, 0, 0, 0);

        return visitDate.getTime() > errorNotificationDate.getTime();
    }, [form.onSiteSupport, form.visitDate, form.errorNotificationDate, isDealer]);

    const validate = useCallback(() => {
        const requiredFields = [
            { value: form.errorNotificationDate, name: th('technicalSupport.fields.errorNotificationDate') },
            ...(requireDealerAddressSelection ? [{ value: form.selectedDealer, name: th('technicalSupport.fields.dealer') }] : []),
            ...(requireDealerAddressSelection ? [{ value: form.selectedDealerAddress, name: th('userAccounts.address') }] : []),
            { value: form.chassisNo, name: th('technicalSupport.fields.chassisNo') },
            { value: form.vehicleWarrantyInf, name: th('technicalSupport.fields.vehicleWarrantyInf') },
            { value: form.vehicleModelEmissions?.trim(), name: th('technicalSupport.fields.vehicleModelEmissions') },
            {
                value: form.vehicleEmissions !== null && form.vehicleEmissions !== undefined,
                name: th('technicalSupport.fields.vehicleEmissions')
            },
            {
                value: form.vehicleGsr !== null && form.vehicleGsr !== undefined,
                name: th('technicalSupport.fields.vehicleGsr')
            },
            {
                value: form.kmInformation !== null && form.kmInformation !== undefined && !isKmInvalid,
                name: th('technicalSupport.fields.kmInformation')
            },
            {
                value: !!form.menzilType,
                name: 'Menzil Type'
            },
            { value: form.vehicleCustomerInf?.trim(), name: th('technicalSupport.fields.vehicleCustomerInf') },
            { value: form.errorDescription?.trim(), name: th('technicalSupport.fields.errorDescription') },
            { value: form.checksPerformed?.trim(), name: th('technicalSupport.fields.checksPerformed') },
            ...(isVorMode ? [] : [{ value: form.errorEffect, name: th('technicalSupport.fields.errorEffect') }]),
            { value: form.cluster?.trim(), name: th('technicalSupport.fields.cluster') },
            { value: form.evcu?.trim(), name: th('technicalSupport.fields.evcu') },
            { value: form.zr5?.trim(), name: th('technicalSupport.fields.zr5') },
            { value: !!form.mainCategory, name: th('technicalSupport.faultCodes.mainCategory') },
            { value: !!form.secondCategory, name: th('technicalSupport.faultCodes.secondCategory') },
            { value: !!form.subCategory, name: th('technicalSupport.faultCodes.subCategory') },
            { value: !!form.failureMode, name: th('technicalSupport.faultCodes.failureMode') },
            { value: form.moreChassis !== null, name: th('technicalSupport.fields.moreChassis') },
            ...(form.moreChassis ? [{ value: (form.additionalChassis || []).length > 0, name: th('technicalSupport.validation.chassisListRequired') }] : [])
        ];

        // Ana şasi politika kontrolü - sadece şasi değiştiğinde veya yeni kayıtta gerekli
        const mainChassisChanged = editMode && form.chassisNo !== form.initialChassisNo;
        const shouldCheckMainChassisPolicy = !editMode || mainChassisChanged;

        if (shouldCheckMainChassisPolicy && Number(form.vehicleWarrantyInf) === 2) {
            requiredFields.push({
                value: form.mainChassisPolicy === 1,
                name: th('technicalSupport.fields.warrantyPolicyAccept')
            });
        }

        if (form.moreChassis && (form.additionalChassis || []).length > 0 && form.additionalChassisHasOutOfWarranty) {
            requiredFields.push({
                value: !!form.additionalChassisWarrantyPolicyAccepted,
                name: th('technicalSupport.fields.warrantyPolicyAccept')
            });
        }

        if (form.problemSolved) {
            requiredFields.push({ value: form.solutionMethod?.trim(), name: th('technicalSupport.fields.solutionMethod') }, { value: form.solutionDate, name: th('technicalSupport.fields.solutionDate') });
        }

        if (form.statusChanged) {
            requiredFields.push({ value: form.statusNotification !== null && form.statusNotification !== undefined, name: th('technicalSupport.fields.statusNotification') });
        }
        if (form.statusChanged || form.actionManagerChanged) {
            requiredFields.push({ value: form.statusDate, name: th('technicalSupport.fields.statusDate') });
        }

        if (form.onSiteSupport) {
            requiredFields.push({ value: !!form.technicalPolicy, name: th('technicalSupport.fields.technicalPolicy') });
        }

        if (form.onSiteSupport && !isDealer) {
            requiredFields.push(
                { value: form.visitDate, name: th('technicalSupport.fields.visitDate') },
                { value: form.visitErrorDesc?.trim(), name: th('technicalSupport.fields.visitErrorDesc') },
                { value: form.rootCause?.trim(), name: th('technicalSupport.fields.rootCause') },
                { value: form.solution?.trim(), name: th('technicalSupport.fields.solution') },
                { value: form.manHour !== null && form.manHour !== undefined, name: th('technicalSupport.fields.manHour') },
                {
                    value: form.totalExpenses !== null && form.totalExpenses !== undefined,
                    name: th('technicalSupport.fields.totalExpenses')
                },
                {
                    value: form.outsourcingExpense === null && form.totalExpenses === null ? true : !!form.expenseCurr,
                    name: th('technicalSupport.fields.expenseCurr')
                }
            );
        }

        if (!isVisitDateAfterErrorNotificationDate()) {
            requiredFields.push({
                value: false,
                name: th('technicalSupport.validation.visitDateAfterErrorNotification')
            });
        }

        if (!isSolutionDateAfterErrorNotificationDate()) {
            requiredFields.push({
                value: false,
                name: th('technicalSupport.validation.solutionDateAfterErrorNotification')
            });
        }

        if (form.ptsCriteria && !isDealer) {
            requiredFields.push({ value: form.ptsNumber?.trim(), name: th('technicalSupport.fields.ptsNumber') });
        }

        const missingFields = requiredFields.filter((f) => !f.value);
        return missingFields.length === 0;
    }, [form, th, isDealer, isVorMode, requireDealerAddressSelection, editMode, isKmInvalid, isSolutionDateAfterErrorNotificationDate, isVisitDateAfterErrorNotificationDate]);

    return { validate, isKmInvalid };
};
